import { getAuth } from "firebase/auth";
import { USERS_API_BASE_URL } from "../config/api";

export interface ResendInviteResult {
  ok: boolean;
  message: string;
}

/**
 * Trigger a password-reset / invite resend for an email address (Trello #415).
 * The backend answers 200 whether or not the account exists, so we can't (and
 * shouldn't) tell the user if the email is registered.
 *
 * ⚠️ The ticket documents this endpoint as Firebase-token-auth'd, but it is
 * called from the login page where the user is NOT signed in — so there is
 * normally no ID token to attach. We send one only if a session happens to
 * exist; otherwise the call goes out unauthenticated. If the backend rejects
 * unauthenticated calls, this endpoint needs to be made callable pre-auth for
 * the forgot-password flow to work.
 */
export const accountApi = {
  async resendInvite(email: string): Promise<ResendInviteResult> {
    const headers: HeadersInit = { "Content-Type": "application/json" };
    try {
      const user = getAuth().currentUser;
      if (user) headers["Authorization"] = `Bearer ${await user.getIdToken()}`;
    } catch {
      /* no session — send unauthenticated */
    }

    const res = await fetch(
      `${USERS_API_BASE_URL}/internal/run-invite-job/resend`,
      {
        method: "POST",
        headers,
        mode: "cors",
        body: JSON.stringify({ email }),
      }
    );

    switch (res.status) {
      case 200:
        return {
          ok: true,
          message: "If an account exists for that email, we've sent a link to set a new password. Check your inbox.",
        };
      case 429:
        return {
          ok: false,
          message: "Too many attempts. Please try again in an hour.",
        };
      case 400:
        return { ok: false, message: "Please enter a valid email address." };
      default:
        return {
          ok: false,
          message: "Couldn't send the reset email. Please try again.",
        };
    }
  },
};
